import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../context/AuthContext';
import { STRIPE_PUBLISHABLE_KEY } from '../firebaseConfig';
import Swal from 'sweetalert2';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const functions = getFunctions(app);
const db = getFirestore(app);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#9e2146' },
  },
};

const AddCardForm = ({ stripeCustomerId, userCollection, userId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      // Create Setup Intent
      const createSetupIntent = httpsCallable(functions, 'createSetupIntent');
      const result = await createSetupIntent({ stripeCustomerId });
      const clientSecret = result.data.clientSecret;

      // Confirm card setup
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            // Could add name/email here if needed
          },
        },
      });

      if (error) {
        Swal.fire('Error', error.message, 'error');
      } else if (setupIntent.status === 'succeeded') {
        Swal.fire('Saved!', 'Your card has been saved.', 'success');
        onSuccess();
      }
    } catch (err) {
      console.error('Error saving card:', err);
      Swal.fire('Error', err.message || 'Failed to save card.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3">
      <div className="form-group">
        <label>Card Details</label>
        <div className="form-control" style={{ padding: '12px', borderRadius: 12 }}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>
      <button type="submit" className="btn btn-primary btn-block" disabled={!stripe || loading} style={{ borderRadius: 12 }}>
        {loading ? (
          <><span className="spinner-border spinner-border-sm mr-2"></span>Saving...</>
        ) : (
          <><i className="fas fa-save mr-2"></i>Save Card</>
        )}
      </button>
    </form>
  );
};

const PaymentMethods = ({ userId, userCollection }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);

  const loadPaymentMethods = async () => {
    setLoading(true);
    try {
      // Get stripeCustomerId from user doc
      const userDoc = await getDoc(doc(db, userCollection, userId));
      const cid = userDoc.data()?.stripeCustomerId;
      setStripeCustomerId(cid);

      if (!cid) {
        setPaymentMethods([]);
        setLoading(false);
        return;
      }

      const getMethods = httpsCallable(functions, 'getStripePaymentMethods');
      const result = await getMethods({ stripeCustomerId: cid });
      setPaymentMethods(result.data.paymentMethods || []);
    } catch (e) {
      console.error('Error loading payment methods:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) loadPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDelete = async (pmId) => {
    const result = await Swal.fire({
      title: 'Remove Card?',
      text: 'Are you sure you want to remove this payment method?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const detachFn = httpsCallable(functions, 'detachPaymentMethod');
      await detachFn({ paymentMethodId: pmId });
      Swal.fire('Removed!', 'Payment method removed.', 'success');
      await loadPaymentMethods();
    } catch (e) {
      console.error('Error removing payment method:', e);
      Swal.fire('Error', e.message || 'Failed to remove card.', 'error');
    }
  };

  const brandIcon = (brand) => {
    const map = {
      visa: 'fa-cc-visa',
      mastercard: 'fa-cc-mastercard',
      amex: 'fa-cc-amex',
      discover: 'fa-cc-discover',
      jcb: 'fa-cc-jcb',
      diners: 'fa-cc-diners-club',
    };
    return map[brand] || 'fa-credit-card';
  };

  return (
    <div className="ff-account-card mt-4">
      <div className="ff-account-header" style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
        <div className="ff-account-info">
          <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
            <i className="fas fa-credit-card mr-2 text-orange"></i>Payment Methods
          </h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Manage your saved cards</p>
        </div>
        <button className="ff-btn ff-btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : <><i className="fas fa-plus mr-1"></i>Add</>}
        </button>
      </div>

      {showForm && (
        <Elements stripe={stripePromise}>
          <AddCardForm
            stripeCustomerId={stripeCustomerId}
            userCollection={userCollection}
            userId={userId}
            onSuccess={() => {
              setShowForm(false);
              loadPaymentMethods();
            }}
          />
        </Elements>
      )}

      <div className="mt-3">
        {loading ? (
          <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-orange"></div></div>
        ) : paymentMethods.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-credit-card fa-2x mb-2" style={{ opacity: 0.3 }}></i>
            <p className="mb-0">No saved cards yet.</p>
          </div>
        ) : (
          paymentMethods.map((pm) => (
            <div key={pm.id} className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
              <div className="d-flex align-items-center gap-3">
                <i className={`fab ${brandIcon(pm.brand)}`} style={{ fontSize: '1.5rem', color: '#555' }}></i>
                <div>
                  <div className="font-weight-bold" style={{ textTransform: 'capitalize' }}>{pm.brand} ending in {pm.last4}</div>
                  <div className="text-muted small">Expires {pm.expMonth}/{pm.expYear}</div>
                </div>
              </div>
              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(pm.id)}>
                <i className="fas fa-trash"></i>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;
